import {
  ELITE_MARCA,
  isEliteBanner,
  stripEliteBannerName,
} from "../constants/eliteSite";

const ELITE_MARKETING_PREFIX = "[EV] ";

const GAC_URL_MARKERS = [
  "grupoalianzacolombia.com",
  "vip.grupoalianzacolombia.com",
];

const ELITE_URL_MARKERS = [
  "elitevacances.com",
  "storage.elitevacances.com",
  "assets.elitevacances.com",
];

const normalizeText = (value = "") =>
  String(value).toLowerCase().trim().replace(/\s+/g, " ");

const recordText = (record = {}) =>
  `${record.promocion || ""} ${record.tipo || ""}`.toLowerCase();

const hasMarker = (text, markers) =>
  markers.some((marker) => text.includes(marker));

const stripEliteMarketingPrefix = (value = "") =>
  String(value).replace(/^\[EV\]\s*/, "").trim();

const tagEliteMarketingText = (value = "") => {
  const clean = stripEliteMarketingPrefix(value);
  return clean ? `${ELITE_MARKETING_PREFIX}${clean}` : clean;
};

const extractBannerLabel = (record = {}) => {
  const raw = stripEliteMarketingPrefix(
    record.promocion || record.tipo || "",
  );
  const match = raw.match(/click en el banner\s+(.+?)(?:\s+con el link|$)/i);
  return match ? normalizeText(match[1]) : "";
};

const extractRecordLink = (record = {}) => {
  const text = `${record.promocion || ""} ${record.tipo || ""}`;
  const match = text.match(/https?:\/\/[^\s]+/i);
  return match ? match[0].toLowerCase() : "";
};

const namesMatch = (label, name) => {
  if (!label || !name) return false;
  if (label === name) return true;
  return label.includes(name) || name.includes(label);
};

const linksMatch = (recordLink, bannerLink) => {
  if (!recordLink || !bannerLink) return false;
  const a = recordLink.toLowerCase();
  const b = bannerLink.toLowerCase();
  return a.includes(b) || b.includes(a);
};

const buildMarketingFilterContext = (banners = []) => {
  const list = Array.isArray(banners) ? banners : [];
  const eliteBanners = list.filter(isEliteBanner);
  const gacBanners = list.filter((banner) => !isEliteBanner(banner));

  const mapNames = (items) =>
    items
      .map((banner) => normalizeText(stripEliteBannerName(banner.name || "")))
      .filter((name) => name.length > 2);

  const mapLinks = (items) =>
    items
      .map((banner) => String(banner.link || "").toLowerCase().trim())
      .filter(Boolean);

  return {
    eliteBannerNames: mapNames(eliteBanners),
    gacBannerNames: mapNames(gacBanners),
    eliteBannerLinks: mapLinks(eliteBanners),
    gacBannerLinks: mapLinks(gacBanners),
  };
};

const matchesBannerNames = (label, names = []) =>
  names.some((name) => namesMatch(label, name));

const matchesBannerLinks = (recordLink, links = []) =>
  links.some((link) => linksMatch(recordLink, link));

const isGacMarketingRecord = (record = {}, context = {}) => {
  const text = recordText(record);
  if (hasMarker(text, GAC_URL_MARKERS)) return true;

  const label = extractBannerLabel(record);
  const link = extractRecordLink(record);
  const { gacBannerNames = [], gacBannerLinks = [] } = context;

  if (matchesBannerNames(label, gacBannerNames)) return true;
  if (matchesBannerLinks(link, gacBannerLinks)) return true;

  return false;
};

const isEliteMarketingRecord = (record = {}, context = {}) => {
  if (isGacMarketingRecord(record, context)) return false;

  if (record.marca === ELITE_MARCA) return true;

  const promo = String(record.promocion || "");
  const tipo = String(record.tipo || "");

  if (
    promo.startsWith(ELITE_MARKETING_PREFIX) ||
    tipo.startsWith(ELITE_MARKETING_PREFIX)
  ) {
    return true;
  }

  const text = recordText(record);
  if (hasMarker(text, ELITE_URL_MARKERS)) return true;

  const label = extractBannerLabel(record);
  const link = extractRecordLink(record);
  const { eliteBannerNames = [], eliteBannerLinks = [] } = context;

  if (matchesBannerNames(label, eliteBannerNames)) return true;
  if (matchesBannerLinks(link, eliteBannerLinks)) return true;

  return false;
};

const filterEliteMarketingRecords = (records = [], context = {}) => {
  const list = Array.isArray(records) ? records : [];
  return list.filter((record) => isEliteMarketingRecord(record, context));
};

const filterGacMarketingRecords = (records = [], context = {}) => {
  const list = Array.isArray(records) ? records : [];
  return list.filter((record) => isGacMarketingRecord(record, context));
};

const buildEliteMarketingPayload = ({
  promocion = "",
  tipo = "",
  año,
  mes,
} = {}) => {
  const taggedPromo = tagEliteMarketingText(promocion);
  const taggedTipo = tagEliteMarketingText(tipo || promocion);

  return {
    promocion: taggedPromo,
    tipo: taggedTipo || taggedPromo,
    marca: ELITE_MARCA,
    año: año ?? new Date().getFullYear().toString(),
    mes: mes ?? (new Date().getMonth() + 1).toString(),
  };
};

export {
  ELITE_MARKETING_PREFIX,
  stripEliteMarketingPrefix,
  tagEliteMarketingText,
  buildMarketingFilterContext,
  isGacMarketingRecord,
  isEliteMarketingRecord,
  filterEliteMarketingRecords,
  filterGacMarketingRecords,
  buildEliteMarketingPayload,
};
